import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorApi } from '../api/cliente'
import { caja as apiCaja, catalogo, ventas as apiVentas } from '../api/servicios'
import { cantidad as formatearCantidad, dinero } from '../api/formato'
import type {
    Categoria,
    OrdenProductos,
    Producto,
    ResultadoResolucion,
    SesionCaja,
    TipoProducto,
    Venta,
    VentaLinea,
} from '../api/tipos'
import { DialogoCantidad } from '../componentes/DialogoCantidad'
import { buscarPorCodigo, cargarCatalogo } from '../api/catalogoLocal'
import { EscanerCamara, hayCamaraPosible } from '../componentes/EscanerCamara'
import { DialogoDescuento } from '../componentes/DialogoDescuento'
import { DialogoCobro } from '../componentes/DialogoCobro'

/**
 * Agrega una linea **provisional**: la que se dibuja al instante, antes de que
 * el servidor conteste.
 *
 * Los identificadores provisionales son negativos para que no se confundan
 * jamas con uno de la base, y para que una accion sobre una linea que todavia
 * no existe se pueda bloquear mirando el signo.
 *
 * Reproduce la regla del servidor de juntar el mismo producto en una sola
 * linea. Si no lo hiciera, el cajero veria aparecer dos lineas y colapsarse en
 * una medio segundo despues, que es peor que esperar.
 */
/**
 * Lo minimo para pintar una linea de venta.
 *
 * Lo cumplen el producto de la grilla y el del catalogo en memoria, que traen
 * campos distintos. Ninguno de los dos aporta nada que el servidor vaya a
 * creerse: el precio viaja solo para mostrarlo.
 */
type ProductoVendible = {
    productoId: number
    sku: string
    nombre: string
    unidadMedidaCodigo: string
    decimales: number
    tipo: TipoProducto
    precioVigente: number | null
}

function conCantidad(venta: Venta, ventaDetalleId: number, cantidad: number): Venta {
    const linea = venta.lineas.find((l) => l.ventaDetalleId === ventaDetalleId)

    if (!linea) return venta

    const importe = cantidad * linea.precioUnitario
    const diferencia = importe - linea.subtotal

    return {
        ...venta,
        lineas: venta.lineas.map((l) =>
            l === linea ? { ...l, cantidad, subtotal: importe, total: importe - l.descuento } : l,
        ),
        subtotal: venta.subtotal + diferencia,
        total: venta.total + diferencia,
    }
}

function sinLinea(venta: Venta, ventaDetalleId: number): Venta {
    const linea = venta.lineas.find((l) => l.ventaDetalleId === ventaDetalleId)

    if (!linea) return venta

    return {
        ...venta,
        lineas: venta.lineas.filter((l) => l.ventaDetalleId !== ventaDetalleId),
        subtotal: venta.subtotal - linea.subtotal,
        descuentoTotal: venta.descuentoTotal - linea.descuento,
        total: venta.total - linea.total,
    }
}

function conLineaProvisional(venta: Venta, producto: ProductoVendible, cantidad: number): Venta {
    const precio = producto.precioVigente ?? 0
    const importe = cantidad * precio
    const existente = venta.lineas.find((l) => l.productoId === producto.productoId)

    const lineas = existente
        ? venta.lineas.map((l) =>
              l === existente
                  ? {
                        ...l,
                        cantidad: l.cantidad + cantidad,
                        subtotal: l.subtotal + importe,
                        total: l.total + importe,
                    }
                  : l,
          )
        : [
              ...venta.lineas,
              {
                  ventaDetalleId: -(venta.lineas.length + 1),
                  numeroLinea: venta.lineas.length + 1,
                  productoId: producto.productoId,
                  sku: producto.sku,
                  descripcion: producto.nombre,
                  unidadMedidaCodigo: producto.unidadMedidaCodigo,
                  cantidad,
                  precioUnitario: precio,
                  descuento: 0,
                  subtotal: importe,
                  total: importe,
              },
          ]

    // Los totales se adelantan sin promociones: el motor de promociones vive en
    // el servidor y aqui no se replica. Si una promocion entra, el total baja
    // cuando llega la respuesta; nunca sube. Preferible a la inversa.
    return {
        ...venta,
        lineas,
        subtotal: venta.subtotal + importe,
        total: venta.total + importe,
    }
}
import './pos.css'

type Dialogo =
    | { tipo: 'ninguno' }
    | { tipo: 'cantidad'; resolucion: ResultadoResolucion; lineaId?: number }
    | { tipo: 'descuento'; linea: VentaLinea | null }
    | { tipo: 'cobro' }
    // La camara es un dialogo y no un panel suelto a proposito: 'enfocarBuscador'
    // solo devuelve el foco cuando no hay ninguno abierto, y en el POS ese
    // retorno de foco es agresivo —hasta en onBlur— porque la pistola escribe
    // donde este el cursor. Sin esto, el POS le pelearia el foco a la camara.
    | { tipo: 'camara' }

export function Pos() {
    const [sesion, setSesion] = useState<SesionCaja | null>(null)
    const [venta, setVenta] = useState<Venta | null>(null)
    const [iniciando, setIniciando] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [ocupado, setOcupado] = useState(false)

    const [texto, setTexto] = useState('')
    const [categoriaId, setCategoriaId] = useState<number | null>(null)
    const [orden, setOrden] = useState<OrdenProductos>('MasVendidos')
    const [categorias, setCategorias] = useState<Categoria[]>([])
    const [grilla, setGrilla] = useState<Producto[]>([])
    const [cargandoGrilla, setCargandoGrilla] = useState(true)

    const [seleccionada, setSeleccionada] = useState<number | null>(null)
    const [dialogo, setDialogo] = useState<Dialogo>({ tipo: 'ninguno' })

    const buscador = useRef<HTMLInputElement>(null)

    /**
     * Si el teclado en pantalla puede abrirse.
     *
     * El foco tiene que quedarse en el buscador para que el lector funcione,
     * pero en una tablet eso abria el teclado en cada producto que se tocaba y
     * tapaba media pantalla. Con `inputMode="none"` el campo sigue enfocado y
     * recibe lo que escribe la pistola, pero el sistema no muestra el teclado.
     * Solo se habilita cuando el cajero toca el campo para escribir un nombre.
     */
    const [tecladoPedido, setTecladoPedido] = useState(false)

    /** En el telefono la venta vive en una hoja que sube desde abajo. */
    const [ventaAbierta, setVentaAbierta] = useState(false)

    /**
     * Las peticiones que modifican la venta salen de a una y en orden.
     *
     * Con tres productos tocados rapido y tres peticiones en vuelo, las
     * respuestas pueden llegar desordenadas y la ultima en llegar —que puede ser
     * la de la primera linea— dejaria la venta como estaba dos productos atras.
     */
    const cola = useRef<Promise<void>>(Promise.resolve())

    /** Cuantas operaciones hay encoladas o en curso. */
    const pendientes = useRef(0)

    /**
     * Encola una operacion sobre la venta y adopta su resultado **solo si ya no
     * queda ninguna otra esperando**.
     *
     * Esa condicion es la que faltaba, y se notaba: tocando tres productos
     * seguidos, las tres lineas se pintaban al instante y de golpe
     * desaparecian dos. La respuesta de la primera llamada trae la venta con
     * una sola linea —en el servidor las otras dos todavia no existian— y
     * adoptarla borraba de la pantalla lo que el cajero acababa de agregar.
     * Despues llegaban las otras dos respuestas y las volvian a pintar.
     *
     * Mientras haya operaciones en vuelo manda lo que se ve, que es lo que el
     * cajero acaba de hacer. La ultima respuesta trae la venta completa y es la
     * unica que se adopta: una sola sustitucion, sin parpadeo.
     */
    function enFila(accion: () => Promise<Venta | null>) {
        pendientes.current += 1

        cola.current = cola.current.then(async () => {
            try {
                const fresca = await accion()

                if (fresca !== null && pendientes.current === 1) {
                    setVenta(fresca)
                }
            } finally {
                pendientes.current -= 1
            }
        })
    }

    /**
     * El foco vive en el buscador. El lector de codigo de barras se comporta
     * como un teclado: escribe el codigo y manda Enter. Si el foco se va a otro
     * lado, el escaneo se pierde en el vacio.
     */
    const enfocarBuscador = useCallback(() => {
        if (dialogo.tipo === 'ninguno') {
            buscador.current?.focus()
        }
    }, [dialogo.tipo])

    // Android decide si abre el teclado en el momento del toque, y para entonces
    // el atributo todavia era 'none'. Reenfocar despues del cambio lo reabre.
    useEffect(() => {
        if (!tecladoPedido) return

        buscador.current?.blur()
        buscador.current?.focus()
    }, [tecladoPedido])

    // El catalogo se baja una vez, al abrir el punto de venta. No bloquea nada:
    // mientras no este, cada escaneo le pregunta al servidor como antes.
    useEffect(() => {
        void cargarCatalogo()
    }, [])

    useEffect(() => {
        let vigente = true

        async function iniciar() {
            try {
                const activa = await apiCaja.sesionActiva()

                if (!vigente) return

                if (!activa) {
                    setSesion(null)
                    return
                }

                setSesion(activa)
                setVenta(await apiVentas.abrir())
            } catch (fallo) {
                if (vigente) setError(mensaje(fallo))
            } finally {
                if (vigente) setIniciando(false)
            }
        }

        void iniciar()

        return () => {
            vigente = false
        }
    }, [])

    // Las categorias cambian poco: se piden una vez y se quedan.
    useEffect(() => {
        catalogo
            .categorias()
            .then(setCategorias)
            .catch(() => {
                // Sin categorias la grilla igual funciona: se pierden los
                // filtros, no la venta.
            })
    }, [])

    useEffect(() => {
        enfocarBuscador()
    }, [venta, dialogo.tipo, enfocarBuscador])

    /**
     * Contenido de la grilla. El buscador la filtra en vez de abrir una lista
     * aparte: dos listas de resultados en la misma pantalla obligan al cajero a
     * decidir en cual mirar.
     */
    useEffect(() => {
        const consulta = texto.trim()
        const control = new AbortController()

        setCargandoGrilla(true)

        const temporizador = window.setTimeout(async () => {
            try {
                const pagina = await catalogo.grillaPos(
                    {
                        texto: consulta.length >= 2 ? consulta : undefined,
                        categoriaId: categoriaId ?? undefined,
                        orden,
                    },
                    control.signal,
                )

                setGrilla(pagina.items)
            } catch {
                // Una busqueda cancelada o fallida no puede interrumpir la
                // venta en curso: la grilla se queda con lo que ya tenia.
            } finally {
                if (!control.signal.aborted) setCargandoGrilla(false)
            }
        }, consulta.length >= 2 ? 220 : 0)

        return () => {
            window.clearTimeout(temporizador)
            control.abort()
        }
    }, [texto, categoriaId, orden])

    function mensaje(fallo: unknown): string {
        return fallo instanceof ErrorApi
            ? fallo.mensajeVisible()
            : 'No se pudo conectar con el servidor.'
    }

    async function ejecutar(accion: () => Promise<Venta>) {
        setOcupado(true)
        setError(null)

        try {
            setVenta(await accion())
        } catch (fallo) {
            setError(mensaje(fallo))
        } finally {
            setOcupado(false)
            enfocarBuscador()
        }
    }

    async function agregarPorCodigo(codigo: string, origen: 'teclado' | 'camara' = 'teclado') {
        if (!venta || codigo.trim() === '') return

        // Si el catalogo lo conoce, no hace falta ningun viaje para pintar la
        // linea: se agrega igual que si se hubiera tocado su tarjeta, y la
        // peticion sale por detras.
        const enCatalogo = buscarPorCodigo(codigo)

        if (enCatalogo) {
            await agregarPorProducto(enCatalogo)
            return
        }

        setOcupado(true)
        setError(null)

        try {
            // Una sola llamada: el servidor resuelve el codigo y agrega la linea.
            // Antes eran dos viajes, y el primero solo servia para saber si el
            // producto se vende por peso —que es la excepcion, no la regla—.
            setVenta(await apiVentas.agregarLinea(venta.ventaId, { codigo: codigo.trim() }))
            setTexto('')
            setTecladoPedido(false)
        } catch (fallo) {
            // Un pesable no se agrega solo: el peso lo teclea el cajero. Recien
            // aqui hace falta el segundo viaje, para poder mostrarle el precio.
            if (fallo instanceof ErrorApi && fallo.codigo === 'cantidad_requerida') {
                setDialogo({ tipo: 'cantidad', resolucion: await apiVentas.resolver(codigo.trim()) })
                setTexto('')
                return
            }

            // Si no es un codigo conocido pero la grilla tiene una coincidencia,
            // se usa esa: el cajero tecleo el nombre, no escaneo.
            //
            // Con la camara, no. Un codigo leido que no esta en el catalogo es
            // un producto que falta darle de alta, y caer en "el primero de la
            // grilla" seria agregar cualquier cosa a la venta sin que nadie se
            // entere hasta cuadrar la caja. Ahi conviene el error.
            const primero = origen === 'teclado' ? grilla[0] : undefined

            if (fallo instanceof ErrorApi && fallo.estado === 404 && primero) {
                await agregarPorProducto(primero)
                return
            }

            setError(mensaje(fallo))
        } finally {
            setOcupado(false)
            enfocarBuscador()
        }
    }

    /**
     * Agrega un producto de la grilla.
     *
     * Antes preguntaba al servidor por el producto y despues lo agregaba: dos
     * viajes de ida y vuelta por cada toque, y en una tienda lejos del servidor
     * eso es lo que se siente como lentitud. La grilla ya trae todo lo que esa
     * primera consulta devolvia —el tipo, el precio vigente, la unidad—, asi
     * que se arma aqui y queda un solo viaje.
     */
    async function agregarPorProducto(producto: ProductoVendible) {
        if (!venta) return

        if (producto.precioVigente === null) {
            setError(`'${producto.nombre}' no tiene precio vigente.`)
            return
        }

        setTexto('')
        setTecladoPedido(false)

        // Un pesable no se agrega solo: el peso lo teclea el cajero.
        if (producto.tipo === 'Pesable') {
            setDialogo({
                tipo: 'cantidad',
                resolucion: {
                    productoId: producto.productoId,
                    sku: producto.sku,
                    nombre: producto.nombre,
                    unidadMedidaCodigo: producto.unidadMedidaCodigo,
                    decimales: producto.decimales,
                    tipo: producto.tipo,
                    precioUnitario: producto.precioVigente,
                    cantidad: null,
                    requiereCantidadManual: true,
                },
            })
            return
        }

        // La linea se dibuja ya, con lo que la grilla ya sabe, y la llamada sale
        // por detras. El cajero no espera al servidor en ningun momento; cuando
        // la respuesta llega, los totales de verdad reemplazan a los adelantados.
        setVenta((actual) => (actual ? conLineaProvisional(actual, producto, 1) : actual))
        setError(null)

        enFila(async () => {
            try {
                return await apiVentas.agregarLinea(venta.ventaId, {
                    productoId: producto.productoId,
                    cantidad: 1,
                })
            } catch (fallo) {
                // Se vuelve a pedir la venta entera en vez de deshacer a mano:
                // tras un fallo, lo unico confiable es lo que diga el servidor.
                setError(mensaje(fallo))

                try {
                    return await apiVentas.obtener(venta.ventaId)
                } catch {
                    return null
                }
            }
        })
    }

    /**
     * Quita una unidad del producto desde su tarjeta. Con una sola, se va la
     * linea entera.
     *
     * Un pesable no entra aqui: su tarjeta no muestra el boton, porque quitar
     * "una unidad" de 0,347 kg no quiere decir nada.
     */
    function quitarUno(producto: ProductoVendible) {
        if (!venta) return

        const linea = venta.lineas.find((l) => l.productoId === producto.productoId)

        if (!linea || linea.ventaDetalleId < 0) return

        if (linea.cantidad <= 1) {
            quitarLinea(linea.ventaDetalleId)
            return
        }

        const nueva = linea.cantidad - 1
        const id = linea.ventaDetalleId

        setVenta((actual) => (actual ? conCantidad(actual, id, nueva) : actual))
        setError(null)

        enFila(async () => {
            try {
                return await apiVentas.cambiarCantidad(venta.ventaId, id, nueva)
            } catch (fallo) {
                setError(mensaje(fallo))

                try {
                    return await apiVentas.obtener(venta.ventaId)
                } catch {
                    return null
                }
            }
        })
    }

    // --- Atajos de teclado -------------------------------------------------

    useEffect(() => {
        function alPulsar(evento: KeyboardEvent) {
            if (dialogo.tipo !== 'ninguno' || !venta) return

            const teclas: Record<string, () => void> = {
                F2: () => {
                    if (venta.lineas.length > 0) setDialogo({ tipo: 'cobro' })
                },
                F4: () => {
                    const linea = venta.lineas.find((l) => l.ventaDetalleId === seleccionada) ?? null
                    if (venta.lineas.length > 0) setDialogo({ tipo: 'descuento', linea })
                },
                F5: () => {
                    const linea = venta.lineas.find((l) => l.ventaDetalleId === seleccionada)
                    if (linea) void abrirCantidad(linea)
                },
                Delete: () => {
                    if (seleccionada && seleccionada > 0) void quitarLinea(seleccionada)
                },
            }

            const accion = teclas[evento.key]

            if (accion) {
                evento.preventDefault()
                accion()
                return
            }

            // Cualquier tecla imprimible devuelve el foco al buscador: el lector
            // puede disparar en cualquier momento.
            if (evento.key.length === 1 && !evento.ctrlKey && !evento.altKey && !evento.metaKey) {
                enfocarBuscador()
            }
        }

        window.addEventListener('keydown', alPulsar)
        return () => window.removeEventListener('keydown', alPulsar)
    })

    async function abrirCantidad(linea: VentaLinea) {
        try {
            const resolucion = await apiVentas.resolverPorId(linea.productoId)
            setDialogo({ tipo: 'cantidad', resolucion, lineaId: linea.ventaDetalleId })
        } catch (fallo) {
            setError(mensaje(fallo))
        }
    }

    /**
     * Quitar sigue el mismo patron que agregar: la linea desaparece de la
     * pantalla en el acto y la peticion sale por detras. Quitar un producto
     * equivocado delante del cliente es de las cosas que mas apuran al cajero,
     * y es justo donde peor caia la espera.
     */
    function quitarLinea(ventaDetalleId: number) {
        // Una linea provisional no existe todavia para el servidor: su boton
        // esta deshabilitado, pero el atajo del teclado podria llegar aqui.
        if (!venta || ventaDetalleId < 0) return

        setSeleccionada(null)
        setVenta((actual) => (actual ? sinLinea(actual, ventaDetalleId) : actual))
        setError(null)

        enFila(async () => {
            try {
                return await apiVentas.quitarLinea(venta.ventaId, ventaDetalleId)
            } catch (fallo) {
                setError(mensaje(fallo))

                try {
                    return await apiVentas.obtener(venta.ventaId)
                } catch {
                    return null
                }
            }
        })
    }

    const lineas = useMemo(() => venta?.lineas ?? [], [venta])
    const hayLineas = lineas.length > 0

    /**
     * Cuanto hay en la venta de cada producto, para mostrarlo sobre su tarjeta.
     *
     * Que el contador viva en la tarjeta es el cambio que mas ahorra: el cajero
     * ve lo que lleva y lo corrige sin salir de la grilla, que es donde esta
     * trabajando.
     */
    const cantidadPorProducto = useMemo(() => {
        const mapa = new Map<number, number>()

        for (const linea of lineas) {
            mapa.set(linea.productoId, (mapa.get(linea.productoId) ?? 0) + linea.cantidad)
        }

        return mapa
    }, [lineas])

    // Cobrada o descartada la venta, la hoja se cierra sola: dejarla abierta
    // sobre una venta vacia obliga a un toque que no aporta nada.
    useEffect(() => {
        if (!hayLineas) setVentaAbierta(false)
    }, [hayLineas])

    const unidadesEnVenta = useMemo(
        () => lineas.reduce((suma, l) => suma + l.cantidad, 0),
        [lineas],
    )

    // Nombre de la promocion que descuenta cada linea, para rotularla en el
    // carro. Los descuentos sin promocion son los que aplico una persona a
    // mano: esos ya se ven en el importe y no llevan etiqueta.
    const promocionPorLinea = useMemo(() => {
        const mapa = new Map<number, string>()

        for (const d of venta?.descuentos ?? []) {
            if (d.promocionId !== null && d.ventaDetalleId !== null) {
                mapa.set(d.ventaDetalleId, d.motivo)
            }
        }

        return mapa
    }, [venta])

    // Solo la que mas ahorra. El backend manda una por linea, pero el cajero
    // actua sobre una a la vez y despues la venta se recalcula sola: tres
    // avisos apilados empujan el boton de cobrar fuera de la pantalla y se
    // leen como ruido justo cuando hay alguien esperando.
    const sugerencia = useMemo(() => {
        const todas = venta?.sugerencias ?? []

        return todas.reduce<(typeof todas)[number] | null>(
            (mejor, s) => (mejor === null || s.ahorro > mejor.ahorro ? s : mejor),
            null,
        )
    }, [venta])

    // --- Estados de pantalla ------------------------------------------------

    if (iniciando) {
        return (
            <div className="fila centro" style={{ padding: 'var(--e6)' }}>
                <span className="cargando" aria-label="Cargando" />
            </div>
        )
    }

    if (!sesion) {
        return (
            <div className="panel columna g4" style={{ maxWidth: '32rem', margin: '0 auto' }}>
                <h1>Caja cerrada</h1>
                <p className="tenue">
                    Para vender hay que abrir la caja primero. Así queda registrado el monto
                    inicial y cada venta entra al arqueo del turno.
                </p>
                <Link to="/caja" className="boton boton-primario boton-grande">
                    Abrir caja
                </Link>
            </div>
        )
    }

    return (
        <div className="pos">
            {/* --- Catalogo --- */}
            <section className="pos-catalogo">
                <div className="pos-chips" role="group" aria-label="Categorías">
                    <button
                        type="button"
                        className={`chip${categoriaId === null ? ' chip-activo' : ''}`}
                        aria-pressed={categoriaId === null}
                        onClick={() => setCategoriaId(null)}
                    >
                        Todos
                    </button>

                    {categorias.map((c) => (
                        <button
                            key={c.categoriaId}
                            type="button"
                            className={`chip${categoriaId === c.categoriaId ? ' chip-activo' : ''}`}
                            aria-pressed={categoriaId === c.categoriaId}
                            onClick={() => setCategoriaId(c.categoriaId)}
                        >
                            {c.nombre}
                        </button>
                    ))}
                </div>

                <div className="pos-barra-busqueda">
                    <div className="pos-buscador">
                        <input
                            ref={buscador}
                            className="campo pos-campo-busqueda"
                            value={texto}
                            onChange={(evento) => setTexto(evento.target.value)}
                            onKeyDown={(evento) => {
                                if (evento.key === 'Enter') {
                                    evento.preventDefault()
                                    void agregarPorCodigo(texto)
                                }

                                if (evento.key === 'Escape' && texto !== '') {
                                    evento.preventDefault()
                                    setTexto('')
                                }
                            }}
                            onBlur={() => window.setTimeout(enfocarBuscador, 0)}
                            onPointerDown={() => setTecladoPedido(true)}
                            inputMode={tecladoPedido ? 'search' : 'none'}
                            placeholder="Escanee el código o escriba el producto"
                            autoComplete="off"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            aria-label="Buscar producto"
                        />
                        {ocupado && <span className="cargando pos-girando" aria-hidden="true" />}
                    </div>

                    {/* La pistola sigue siendo el camino principal y no cambia:
                        escribe en el campo de al lado y manda Enter. Esto es
                        para el local que todavia no la compro. */}
                    {hayCamaraPosible() && (
                        <button
                            type="button"
                            className="boton pos-boton-camara"
                            onClick={() => setDialogo({ tipo: 'camara' })}
                            disabled={!venta}
                            title="Escanear con la cámara"
                        >
                            Cámara
                        </button>
                    )}

                    <div className="pos-orden" role="group" aria-label="Orden de la grilla">
                        <button
                            type="button"
                            className={`chip${orden === 'MasVendidos' ? ' chip-activo' : ''}`}
                            aria-pressed={orden === 'MasVendidos'}
                            onClick={() => setOrden('MasVendidos')}
                            title="Los que más se venden primero"
                        >
                            Más vendidos
                        </button>
                        <button
                            type="button"
                            className={`chip${orden === 'Nombre' ? ' chip-activo' : ''}`}
                            aria-pressed={orden === 'Nombre'}
                            onClick={() => setOrden('Nombre')}
                            title="Orden alfabético"
                        >
                            A–Z
                        </button>
                    </div>
                </div>

                {error && (
                    <p className="aviso aviso-error" role="alert" onClick={() => setError(null)}>
                        {error}
                    </p>
                )}

                <div className="pos-grilla-envoltura">
                    {cargandoGrilla && grilla.length === 0 ? (
                        <div className="fila centro" style={{ padding: 'var(--e6)' }}>
                            <span className="cargando" aria-label="Cargando productos" />
                        </div>
                    ) : grilla.length === 0 ? (
                        <p className="vacio">
                            Ningún producto coincide.
                            <br />
                            <span className="diminuto">
                                Si tiene el código en la mano, escanéelo: se agrega igual.
                            </span>
                        </p>
                    ) : (
                        <ul className="pos-grilla">
                            {grilla.map((producto) => (
                                <li key={producto.productoId}>
                                    <TarjetaProducto
                                        producto={producto}
                                        deshabilitado={ocupado}
                                        cantidad={cantidadPorProducto.get(producto.productoId) ?? 0}
                                        alElegir={() => void agregarPorProducto(producto)}
                                        alQuitar={() => quitarUno(producto)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            {/* Barra de la venta. Solo en pantalla angosta: ahi el catalogo se
                queda con toda la pantalla y la venta se consulta desde aqui. */}
            <button
                type="button"
                className="pos-barra-venta"
                onClick={() => setVentaAbierta(true)}
                disabled={!hayLineas}
            >
                <span className="pos-barra-cuenta numero">
                    {formatearCantidad(unidadesEnVenta, 0)}
                </span>
                <span className="pos-barra-texto">
                    {hayLineas ? 'Ver venta' : 'Sin productos'}
                </span>
                <span className="pos-barra-total numero">{dinero(venta?.total ?? 0)}</span>
            </button>

            {/* El fondo oscuro solo existe con la hoja abierta, y cerrarla al
                tocarlo es lo que espera cualquiera en un telefono. */}
            {ventaAbierta && (
                <div
                    className="pos-fondo-hoja"
                    role="presentation"
                    onClick={() => setVentaAbierta(false)}
                />
            )}

            {/* --- Venta en curso --- */}
            <aside className={`pos-venta${ventaAbierta ? ' pos-venta-abierta' : ''}`}>
                {/* Asa de la hoja. Es un boton entero y no un adorno: la franja
                    de arriba es lo primero que toca el pulgar para volver al
                    catalogo, y una barrita sola no se entiende como tocable. */}
                <button
                    type="button"
                    className="pos-hoja-asa"
                    onClick={() => setVentaAbierta(false)}
                    aria-label="Seguir agregando productos"
                >
                    <span className="pos-hoja-barra" aria-hidden="true" />
                    <span className="pos-hoja-texto">Seguir agregando</span>
                </button>

                <div className="pos-lineas">
                    {!hayLineas ? (
                        <p className="vacio">
                            Escanee o toque un producto para comenzar.
                            <br />
                            <span className="diminuto">
                                Enter agrega · F2 cobra · F4 descuenta · F5 cambia cantidad
                            </span>
                        </p>
                    ) : (
                        <table className="tabla pos-tabla">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th className="derecha">Cant.</th>
                                    <th className="derecha">Total</th>
                                    <th aria-label="Acciones" />
                                </tr>
                            </thead>
                            <tbody>
                                {lineas.map((linea) => {
                                    // Identificador negativo: la linea esta dibujada pero el
                                    // servidor todavia no la confirmo. Quitarla o cambiarle la
                                    // cantidad exige un id de verdad, asi que esas dos esperan.
                                    const provisional = linea.ventaDetalleId < 0

                                    return (
                                    <tr
                                        key={linea.ventaDetalleId}
                                        className={`${
                                            seleccionada === linea.ventaDetalleId ? 'fila-activa' : ''
                                        }${provisional ? ' pos-linea-provisional' : ''}`}
                                        onClick={() =>
                                            !provisional && setSeleccionada(linea.ventaDetalleId)
                                        }
                                    >
                                        <td>
                                            <div className="columna">
                                                <span className="truncar" title={linea.descripcion}>
                                                    {linea.descripcion}
                                                </span>
                                                <span className="tenue diminuto numero">
                                                    {dinero(linea.precioUnitario)} c/u
                                                </span>
                                                {promocionPorLinea.has(linea.ventaDetalleId) && (
                                                    <span
                                                        className="insignia insignia-ok truncar"
                                                        title={promocionPorLinea.get(
                                                            linea.ventaDetalleId,
                                                        )}
                                                    >
                                                        {promocionPorLinea.get(linea.ventaDetalleId)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="derecha numero">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma pos-cantidad"
                                                disabled={provisional}
                                                onClick={() => void abrirCantidad(linea)}
                                                aria-label={`Cambiar cantidad de ${linea.descripcion}`}
                                            >
                                                {formatearCantidad(
                                                    linea.cantidad,
                                                    linea.unidadMedidaCodigo === 'UND' ? 0 : 3,
                                                )}
                                            </button>
                                        </td>
                                        <td className="derecha numero negrita">
                                            {dinero(linea.total)}
                                            {linea.descuento > 0 && (
                                                <div className="diminuto pos-descuento">
                                                    −{dinero(linea.descuento)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="derecha">
                                            <button
                                                type="button"
                                                className="boton boton-fantasma"
                                                disabled={provisional}
                                                onClick={() => void quitarLinea(linea.ventaDetalleId)}
                                                aria-label={`Quitar ${linea.descripcion}`}
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {sugerencia && (
                    <p className="aviso aviso-info diminuto pos-sugerencia">
                        <strong>{sugerencia.descripcion}</strong>: con{' '}
                        {formatearCantidad(
                            sugerencia.faltaCantidad,
                            sugerencia.unidadMedidaCodigo === 'UND' ? 0 : 3,
                        )}{' '}
                        más entra en «{sugerencia.promocion}» y paga{' '}
                        {dinero(sugerencia.ahorro)} menos.
                    </p>
                )}

                <div className="pos-pie">
                    {venta && venta.descuentoTotal > 0 && (
                        <>
                            <div className="fila entre chico tenue">
                                <span>Subtotal</span>
                                <span className="numero">{dinero(venta.subtotal)}</span>
                            </div>
                            <div className="fila entre chico pos-descuento">
                                <span>Descuentos</span>
                                <span className="numero">−{dinero(venta.descuentoTotal)}</span>
                            </div>
                        </>
                    )}

                    {venta?.clienteNombre && (
                        <div className="fila entre chico tenue">
                            <span>Cliente</span>
                            <span className="truncar">{venta.clienteNombre}</span>
                        </div>
                    )}

                    <div className="fila entre pos-total">
                        <span>Total</span>
                        <strong className="numero">{dinero(venta?.total ?? 0)}</strong>
                    </div>

                    <button
                        type="button"
                        className="boton boton-primario boton-grande boton-bloque"
                        disabled={!hayLineas || ocupado}
                        onClick={() => setDialogo({ tipo: 'cobro' })}
                    >
                        Cobrar <span className="oculto-movil diminuto">· F2</span>
                    </button>

                    <div className="fila g2">
                        <button
                            type="button"
                            className="boton crece"
                            disabled={!hayLineas || ocupado}
                            onClick={() =>
                                setDialogo({
                                    tipo: 'descuento',
                                    linea: lineas.find((l) => l.ventaDetalleId === seleccionada) ?? null,
                                })
                            }
                        >
                            Descuento
                        </button>

                        <button
                            type="button"
                            className="boton boton-peligro crece"
                            disabled={!hayLineas || ocupado}
                            onClick={() => {
                                if (venta) {
                                    void ejecutar(async () => {
                                        await apiVentas.descartar(venta.ventaId)
                                        return apiVentas.abrir()
                                    })
                                }
                            }}
                        >
                            Descartar
                        </button>
                    </div>
                </div>
            </aside>

            {/* --- Diálogos --- */}
            {dialogo.tipo === 'cantidad' && venta && (
                <DialogoCantidad
                    nombre={dialogo.resolucion.nombre}
                    unidad={dialogo.resolucion.unidadMedidaCodigo}
                    decimales={dialogo.resolucion.decimales}
                    precioUnitario={dialogo.resolucion.precioUnitario}
                    alCerrar={() => setDialogo({ tipo: 'ninguno' })}
                    alConfirmar={(valor) => {
                        const lineaId = dialogo.lineaId
                        setDialogo({ tipo: 'ninguno' })

                        void ejecutar(() =>
                            lineaId
                                ? apiVentas.cambiarCantidad(venta.ventaId, lineaId, valor)
                                : apiVentas.agregarLinea(venta.ventaId, {
                                      productoId: dialogo.resolucion.productoId,
                                      cantidad: valor,
                                  }),
                        )
                    }}
                />
            )}

            {dialogo.tipo === 'descuento' && venta && (
                <DialogoDescuento
                    lineaDescripcion={dialogo.linea?.descripcion ?? null}
                    base={dialogo.linea?.total ?? venta.total}
                    alCerrar={() => setDialogo({ tipo: 'ninguno' })}
                    alConfirmar={(datos) => {
                        const detalleId = dialogo.linea?.ventaDetalleId
                        setDialogo({ tipo: 'ninguno' })

                        void ejecutar(() =>
                            apiVentas.aplicarDescuento(venta.ventaId, {
                                ventaDetalleId: detalleId,
                                ...datos,
                            }),
                        )
                    }}
                />
            )}

            {dialogo.tipo === 'camara' && venta && (
                <EscanerCamara
                    modo="continuo"
                    titulo="Escanear productos"
                    // Entra por la misma puerta que la pistola: mismo resolver,
                    // mismo manejo de pesables, mismo respaldo por nombre.
                    alLeer={(codigo) => void agregarPorCodigo(codigo, 'camara')}
                    alCerrar={() => setDialogo({ tipo: 'ninguno' })}
                />
            )}

            {dialogo.tipo === 'cobro' && venta && (
                <DialogoCobro
                    venta={venta}
                    alCerrar={() => setDialogo({ tipo: 'ninguno' })}
                    alCobrar={async () => {
                        setDialogo({ tipo: 'ninguno' })
                        setSeleccionada(null)
                        // Venta nueva y lista para el siguiente cliente.
                        await ejecutar(() => apiVentas.abrir())
                    }}
                />
            )}
        </div>
    )
}

/* -------------------------------------------------------------------------- */

/** Iniciales del producto, para cuando todavia no tiene foto. */
function iniciales(nombre: string): string {
    return nombre
        .split(/\s+/)
        .filter((palabra) => palabra.length > 0)
        .slice(0, 2)
        .map((palabra) => palabra[0]!.toUpperCase())
        .join('')
}

function TarjetaProducto({
    producto,
    deshabilitado,
    cantidad,
    alElegir,
    alQuitar,
}: {
    producto: Producto
    deshabilitado: boolean
    /** Cuanto hay de este producto en la venta. Cero si no esta. */
    cantidad: number
    alElegir: () => void
    alQuitar: () => void
}) {
    const [falloImagen, setFalloImagen] = useState(false)
    const conFoto = producto.imagenUrl !== null && !falloImagen

    const enVenta = cantidad > 0

    // El boton de quitar no puede ir dentro del de agregar —un boton dentro de
    // otro no es valido—, asi que van como hermanos sobre un contenedor comun.
    return (
        <div className={`pos-tarjeta-caja${enVenta ? ' pos-tarjeta-en-venta' : ''}`}>
        <button
            type="button"
            className="pos-tarjeta"
            disabled={deshabilitado}
            onClick={alElegir}
            title={`${producto.nombre} — ${producto.sku}`}
        >
            <span className="pos-tarjeta-imagen">
                {conFoto ? (
                    <img
                        src={producto.imagenUrl!}
                        alt=""
                        loading="lazy"
                        // Si el archivo se borro del disco, la tarjeta cae al
                        // marcador en vez de mostrar el icono de imagen rota.
                        onError={() => setFalloImagen(true)}
                    />
                ) : (
                    <span className="pos-tarjeta-inicial" aria-hidden="true">
                        {iniciales(producto.nombre)}
                    </span>
                )}

                {producto.tipo === 'Pesable' && (
                    <span className="pos-tarjeta-etiqueta">por {producto.unidadMedidaCodigo}</span>
                )}
            </span>

            <span className="pos-tarjeta-nombre">{producto.nombre}</span>
            <span className="pos-tarjeta-precio numero">{dinero(producto.precioVigente)}</span>
        </button>

        {enVenta && (
            <span className="pos-tarjeta-contador numero" aria-hidden="true">
                {formatearCantidad(cantidad, producto.tipo === 'Pesable' ? 3 : 0)}
            </span>
        )}

        {/* En un pesable, "quitar una unidad" no quiere decir nada: la cantidad
            se corrige desde la linea, con el teclado. */}
        {enVenta && producto.tipo !== 'Pesable' && (
            <button
                type="button"
                className="pos-tarjeta-menos"
                disabled={deshabilitado}
                onClick={alQuitar}
                aria-label={`Quitar uno de ${producto.nombre}`}
            >
                −
            </button>
        )}
        </div>
    )
}
